import React from 'react';
import { StarIcon } from '@heroicons/react/24/solid';

// Wikimedia Commons images resolve through the stable Special:FilePath redirect.
const img = (file: string, width = 1000) =>
  `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(file)}?width=${width}`;

type Spot = {
  title: string;
  description: string;
  image: string;
  link: string;
  linkLabel: string;
  done?: boolean;
};

type Section = {
  id: string;
  heading: string;
  blurb: string;
  spots: Spot[];
};

const sections: Section[] = [
  {
    id: 'eat',
    heading: 'Where to Eat',
    blurb:
      'Stanley is tiny but the food punches way above its weight — from a James Beard–nominated kitchen to legendary cinnamon rolls.',
    spots: [
      {
        title: 'Sawtooth Hotel',
        description:
          'A restored 1931 hotel in downtown Stanley serving thoughtful, creative dishes. Grab a seat on the deck for mountain views with dinner.',
        image: img('Stanley, Idaho (15182187451).jpg'),
        link: 'https://www.sawtoothhotel.com/',
        linkLabel: 'Visit website',
      },
      {
        title: 'Stanley Baking Co. & Cafe',
        description:
          'The go-to breakfast spot — flaky croissants, warm cinnamon rolls, hearty breakfast plates, and strong coffee. Expect a happy little line.',
        image: img('Stanley Idaho 4393.jpg'),
        link: 'https://www.stanleybakingco.com/',
        linkLabel: 'Visit website',
      },
      {
        title: 'Stanley Supper Club & Kasino Club',
        description:
          'A longtime local favorite for a relaxed dinner and drinks — classic American fare with a friendly, low-key mountain-town vibe.',
        image: img('Stanley Idaho 4394.jpg'),
        link: 'https://www.tripadvisor.com/Restaurants-g35609-Stanley_Idaho.html',
        linkLabel: 'See reviews',
      },
    ],
  },
  {
    id: 'hike',
    heading: 'Hikes in the Sawtooths',
    blurb:
      'The Sawtooth Wilderness has some of the most spectacular alpine scenery in the country. Here are a couple of easygoing options — plus the one we did ourselves.',
    spots: [
      {
        title: 'Sawtooth Lake via Iron Creek Trail',
        description:
          'The crown jewel — about 10 miles round trip to a stunning alpine lake beneath Mount Regan. A bit of a climb, but unforgettable.',
        image: img('Sawtooth Lake summer.jpg'),
        link: 'https://www.alltrails.com/trail/us/idaho/sawtooth-lake-via-iron-creek-trail',
        linkLabel: 'Trail details',
        done: true,
      },
      {
        title: 'Redfish Lake Loop',
        description:
          'A mellow, mostly-flat trail tracing the shoreline of Redfish Lake through the pines. Go as far as you like — gorgeous water and mountain views the whole way.',
        image: img('Trail Along Redfish Lake (14891543959).jpg'),
        link: 'https://www.alltrails.com/trail/us/idaho/redfish-lake-loop',
        linkLabel: 'Trail details',
      },
      {
        title: 'Fishhook Creek Trail',
        description:
          'An easy, family-friendly walk — about 4 to 5 miles round trip along Fishhook Creek to a glorious meadow with big mountain views. Perfect for a relaxed morning.',
        image: img('Sawtooth from Stanley Lake Creek.JPG'),
        link: 'https://www.alltrails.com/trail/us/idaho/fishhook-creek-trail',
        linkLabel: 'Trail details',
      },
    ],
  },
  {
    id: 'water',
    heading: 'On the Water',
    blurb: 'Stanley sits at the headwaters of the Salmon River, with Redfish Lake just down the road.',
    spots: [
      {
        title: 'Redfish Lake Scenic Boat Tour',
        description:
          'A relaxed cruise across Redfish Lake from the marina, with narration and incredible Sawtooth views. Tours run a few times daily in summer.',
        image: img('Boats in Redfish Lake.jpg'),
        link: 'https://redfishlake.com/marina/',
        linkLabel: 'Marina & tours',
      },
      {
        title: 'Salmon River Whitewater Rafting',
        description:
          'The "River of No Return" offers everything from gentle floats to Class IV rapids. Local outfitters run half-day and full-day guided trips.',
        image: img('Rafting the Salmon River ID.jpg'),
        link: 'https://stanleycc.org/do/river-rafting/',
        linkLabel: 'Rafting outfitters',
      },
    ],
  },
  {
    id: 'springs',
    heading: 'Hot Springs',
    blurb:
      'Soaking in a natural hot spring under the Sawtooths is a Stanley rite of passage. A few worth seeking out:',
    spots: [
      {
        title: 'Mountain Village Resort Hot Spring',
        description:
          'A private natural hot spring pool right in Stanley, available to Mountain Village Resort guests — the easiest soak in town.',
        image: img('Stitched shot of the Sawtooth Mountains near Stanley, Idaho.jpg'),
        link: 'https://www.mountainvillage.com/hot-springs/',
        linkLabel: 'Resort website',
        done: true,
      },
      {
        title: 'Sunbeam Hot Springs',
        description:
          'Roadside springs along the Salmon River about 13 miles east of Stanley — mix the hot source water with the river to find your perfect temperature.',
        image: img('Salmon River near Lower Stanley, Idaho (15206355646).jpg'),
        link: 'https://stanleycc.org/do/hot-springs/',
        linkLabel: 'Hot springs guide',
      },
      {
        title: 'Boat Box Hot Spring',
        description:
          'A quirky "witch\'s cauldron" — a single iron-tub spring perched right beside the Salmon River. Tiny, scenic, and a local legend.',
        image: img('Salmon River near Lower Stanley, Idaho (15206355646).jpg', 1100),
        link: 'https://stanleycc.org/do/hot-springs/',
        linkLabel: 'Hot springs guide',
      },
    ],
  },
  {
    id: 'more',
    heading: 'More Ideas',
    blurb: 'A few other ways to soak up the Sawtooths while you are in town.',
    spots: [
      {
        title: 'Stargazing in the Dark Sky Reserve',
        description:
          'Central Idaho is the first International Dark Sky Reserve in the U.S. On a clear night the Milky Way over the Sawtooths is breathtaking.',
        image: img('Redfish Lake at Night.jpg'),
        link: 'https://idahodarksky.org/',
        linkLabel: 'Learn more',
      },
      {
        title: 'Sawtooth Scenic Byway',
        description:
          'One of the most beautiful drives in America — pull-offs, overlooks, and the full Sawtooth skyline along Highway 75.',
        image: img('Stitched shot of the Sawtooth Mountains over Redfish Lake, Idaho.jpg'),
        link: 'https://visitidaho.org/things-to-do/road-trips/sawtooth-scenic-byway/',
        linkLabel: 'Plan the drive',
      },
      {
        title: 'Day Trip to Sun Valley & Ketchum',
        description:
          'About a 40-minute drive south over Galena Summit — a charming resort town with great shopping, dining, galleries, and the historic Sun Valley Resort.',
        image: img('Ketchum, Idaho street July 2024.jpg'),
        link: 'https://visitsunvalley.com/',
        linkLabel: 'Explore Sun Valley',
      },
    ],
  },
];

const SpotCard: React.FC<{ spot: Spot }> = ({ spot }) => (
  <div className="wedding-card !p-0 overflow-hidden flex flex-col">
    <div className="relative">
      <img
        src={spot.image}
        alt={spot.title}
        loading="lazy"
        className="h-52 w-full object-cover"
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = 'none';
        }}
      />
      {spot.done && (
        <div className="absolute top-3 left-3 flex items-center gap-1 rounded-full bg-white/95 px-3 py-1 shadow-sm">
          <StarIcon className="h-4 w-4 text-yellow-500" />
          <span className="text-xs font-medium text-gray-800">Couple's pick</span>
        </div>
      )}
    </div>
    <div className="p-6 flex flex-col flex-grow">
      <h3 className="text-xl font-wedding text-gray-900 flex items-start gap-1.5">
        {spot.done && (
          <StarIcon className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-1" aria-hidden="true" />
        )}
        <span>{spot.title}</span>
      </h3>
      {spot.done && (
        <p className="text-sm text-primary-700 font-medium mt-1">
          Natalie &amp; Avarokin have done this!
        </p>
      )}
      <p className="text-gray-600 mt-2 flex-grow">{spot.description}</p>
      <a
        href={spot.link}
        target="_blank"
        rel="noopener noreferrer"
        className="wedding-button-outline text-sm px-3 py-2 mt-4 self-start"
      >
        {spot.linkLabel} →
      </a>
    </div>
  </div>
);

const ThingsToDo: React.FC = () => {
  return (
    <div className="py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-wedding text-gray-900 mb-6">
            Things to Do in Stanley
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Make a trip of it! Stanley, Idaho and the Sawtooth Mountains are full of
            adventure. Here are some of our favorite ways to explore — look for the{' '}
            <StarIcon className="inline h-5 w-5 text-yellow-500 -mt-1" aria-hidden="true" />{' '}
            for spots we have done ourselves.
          </p>
        </div>

        {sections.map((section) => (
          <section key={section.id} className="mb-16">
            <div className="max-w-3xl mx-auto text-center mb-8">
              <h2 className="text-3xl font-wedding text-gray-900 mb-3">{section.heading}</h2>
              <p className="text-gray-600">{section.blurb}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {section.spots.map((spot) => (
                <SpotCard key={spot.title} spot={spot} />
              ))}
            </div>
          </section>
        ))}

        <p className="text-center text-sm text-gray-500">
          Conditions and hours change with the seasons — please check each spot's website
          before heading out.
        </p>
      </div>
    </div>
  );
};

export default ThingsToDo;
